import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router'

import { Subscription } from "rxjs"
import { ThingService } from '../thing.service'
import { DeviceService } from '../device.service'

import { Schedule, Weekday, Time } from './schedule.model'

@Component({
    selector: 'app-schedules',
    templateUrl: './schedules.component.html',
    styleUrls: ['./schedules.component.css']
})
export class SchedulesComponent implements OnInit, OnDestroy {
    // Listen route change, device change is done using url change
    private routeSubscription: Subscription
    private thingReadySubscription: Subscription
    private thingUpdateSubscription: Subscription

    private static MAX_SCHEDULES: number = 10
    private clientToken: string
    public selectedThing: any
    public schedules: Schedule[] = []
    public selectedSchedule: Schedule
    public datePickerVisible: boolean = false
    public loading: boolean = false

    constructor(private route: ActivatedRoute, private router: Router, private thingService: ThingService) {
    }

    ngOnInit() {
        // In case of refresh or direct url navigation, the thing states are not available immediately and we need
        // to wait. BUT this observer is only triggered when initializing mqtt connection to things (meaning that
        // if initalization is done already somewhere else, this will not trigger at all).
        this.thingReadySubscription = this.thingService.readyObservable.subscribe(thingName => {
            if (this.selectedThing && this.selectedThing.name === thingName) {
                this.schedules = this.loadThingSchedules(this.selectedThing)
                this.loading = false
            }
        })
        // Checks when device reports updated schedule and marks it as synced=true.
        this.thingUpdateSubscription = this.thingService.thingsObservable.subscribe(thing => {
            if (this.selectedThing && this.selectedThing.name === thing.name) {
                let reportedSchedules: Schedule[] = this.loadThingSchedules(thing)
                reportedSchedules.forEach(thingSchedule => {
                    let index: number = this.schedules.findIndex(s => s.id === thingSchedule.id)
                    if (index >= 0) {
                        let schedule: Schedule = this.schedules[index]
                        if (schedule.equals(thingSchedule)) {
                            schedule.synced = true
                        }
                    }
                })
            }
        })

        // TODO  what if there are no devices at all?
        this.routeSubscription = this.route.params.subscribe(params => {
            if (params['thing']) {
                this.loading = true
                let routeThing = this.thingService.getThingByName(params['thing'])
                if (routeThing && routeThing.controls === true) {
                    this.selectedThing = routeThing
                    this.schedules = this.loadThingSchedules(this.selectedThing)
                } else {
                    this.router.navigate(["dashboard"])
                }
                this.selectedSchedule = undefined
            } else {
                if (this.thingService.things && this.thingService.things.length > 0) {
                    let things = this.thingService.things.filter(t => t.controls)
                    if (things.length > 0) {
                        let defaultThing = things[0]
                        this.router.navigate(['/schedules', defaultThing.name], { replaceUrl: true })
                    } else {
                        this.router.navigate(["dashboard"])
                    }
                } else {
                    this.router.navigate(["dashboard"])
                }
                this.selectedSchedule = undefined
            }
        })
    }

    ngOnDestroy() {
        if (this.thingReadySubscription) {
            this.thingReadySubscription.unsubscribe()
        }
        if (this.thingUpdateSubscription) {
            this.thingUpdateSubscription.unsubscribe()
        }
        if (this.routeSubscription) {
            this.routeSubscription.unsubscribe()
        }
    }

    public listThings(): any[] {
        return this.thingService.things.filter(t => t.controls)
    }

    public selectSchedule(schedule: Schedule) {
        this.selectedSchedule = schedule.copyOf()
        this.datePickerVisible = false
    }

    public newScheduleAllowed(): boolean {
        return (this.schedules.length < SchedulesComponent.MAX_SCHEDULES) && !this.selectedSchedule && !this.loading
    }

    public newSchedule(): void {
        if (this.newScheduleAllowed()) {
            let now: Date = new Date()
            let end: Date = new Date(now.getTime())
            end.setHours(end.getHours() + 2)

            this.selectedSchedule = new Schedule(
                this.findFreeSlot(this.schedules),
                true,
                new Date(),
                new Date(),
                new Time(now.getHours(), now.getMinutes()),
                new Time(end.getHours(), end.getMinutes()),
                [
                    new Weekday(0, false),
                    new Weekday(1, false),
                    new Weekday(2, false),
                    new Weekday(3, false),
                    new Weekday(4, false),
                    new Weekday(5, false),
                    new Weekday(6, false)
                ],
                70,
                0, // TODO if supported by the device, set proper default
                false
            )
        }
    }

    public removeSchedule(schedule: Schedule): void {
        if (!schedule) {
            return
        }
        let index = this.schedules.findIndex(s => s.id === schedule.id)
        if (index >= 0) {
            this.schedules.splice(index, 1)
            if (this.selectedSchedule && schedule.id === this.selectedSchedule.id) {
                this.selectedSchedule = undefined
            }
            this.updateShadowSchedules()
        }
    }

    private findFreeSlot(schedules: Schedule[]): string {
        for (let i = 0; i < SchedulesComponent.MAX_SCHEDULES; i++) {
            let slot: string = 'sh' + (i + 1)
            let index: number = this.schedules.findIndex(s => s.id === slot)
            if (index >= 0) {
                let schedule: Schedule = this.schedules[index]
                if (!Schedule.validate(schedule.toShadow())) {
                    return slot
                }
            } else {
                return slot
            }
        }
    }

    public saveScheduleEdit(): void {
        if (!this.validateSchedule(this.selectedSchedule)) {
            return
        }

        // Remove any weekday selections from single day schedule
        if (this.selectedSchedule.endsInSameDay()) {
            this.selectedSchedule.weekdays.forEach(wd => {
                wd.enabled = false
            })
        }

        let index = this.schedules.findIndex(s => s.id === this.selectedSchedule.id)
        this.selectedSchedule.synced = false
        if (index >= 0) {
            this.schedules[index] = this.selectedSchedule.copyOf()
        } else {
            this.schedules.push(this.selectedSchedule.copyOf())
        }
        this.selectedSchedule = undefined

        this.sort(this.schedules)
        this.updateShadowSchedules()
    }

    private validateSchedule(schedule: Schedule): boolean {
        if (!schedule) {
            return true
        }
        if (!this.validateEndDate(schedule)) {
            console.log('end date is not valid')
            return false
        }
        if (!this.validateWeekdays(schedule)) {
            console.log('weekdays is not valid')
            return false
        }
        if (!this.validateOverlaps(schedule, this.schedules)) {
            console.log('schedule overlaps with existing schedules')
            return false
        }
        return true
    }

    public validateEndDate(schedule: Schedule): boolean {
        if (!schedule.endDate) {
            return false
        }
        return schedule.endDate.getTime() >= schedule.startDate.getTime()
    }

    public validateWeekdays(schedule: Schedule): boolean {
        if (schedule.endsInSameDay()) {
            return true
        }
        return schedule.weekdays.filter(wd => wd.enabled === true).length > 0
    }

    public validateOverlaps(schedule: Schedule, existingSchedules: Schedule[]): boolean {
        return existingSchedules.filter(ex => {
            return ex.id !== schedule.id && ex.overlaps(schedule)
        }).length === 0
    }

    public formatTime(input: Time): string {
        return Time.format(input)
    }

    public parseTime(field: Time, input: string): void {
        let parsedValue: Time = Time.parse(input)
        if (parsedValue) {
            field.hours = parsedValue.hours
            field.minutes = parsedValue.minutes
        }
    }

    private updateShadowSchedules(): void {
        let payload: any = {}
        payload.state = {}
        payload.state.desired = {}
        for (let i = 0; i < SchedulesComponent.MAX_SCHEDULES; i++) {
            let slot: string = 'sh' + (i + 1)
            let index: number = this.schedules.findIndex(s => s.id === slot)
            if (index >= 0) {
                let schedule: Schedule = this.schedules[index]
                payload.state.desired[schedule.id] = schedule.toShadow()
            } else {
                payload.state.desired[slot] = ''
            }
        }
        this.thingService.updateShadow(this.selectedThing.name, payload)
    }

    public cancelScheduleEdit(): void {
        this.selectedSchedule = undefined
    }

    public toggleChange(schedule: Schedule): void {
        schedule.enabled = !schedule.enabled
        let payload: any = {}
        payload.state = {}
        payload.state.desired = {}
        payload.state.desired[schedule.id] = schedule.toShadow()
        this.thingService.updateShadow(this.selectedThing.name, payload)
    }

    public toggleDatePicker(): void {
        this.datePickerVisible = !this.datePickerVisible
    }

    public startDateChanged(): void {
        if (this.selectedSchedule.startDate.getTime() > this.selectedSchedule.endDate.getTime()) {
            this.selectedSchedule.endDate = new Date(this.selectedSchedule.startDate.getTime())
        }
    }

    public isScheduleRunning(schedule: Schedule): boolean {
        if (this.selectedThing && this.selectedThing.stateObject && schedule) {
            let prField = this.selectedThing.stateObject['pr1']
            if (prField !== undefined) {
                return 'sh' + prField === schedule.id
            }
        }
        return false
    }

    private loadThingSchedules(thing: any): Schedule[] {
        let schedules: Schedule[] = []
        let stateObject: any = thing.stateObject
        if (Object.keys(stateObject).length === 0 && stateObject.constructor === Object) {
            return schedules
        }

        for (let i = 0; i < SchedulesComponent.MAX_SCHEDULES; i++) {
            let scheduleStr: string = stateObject['sh' + (i + 1)]
            if (Schedule.validate(scheduleStr)) {
                schedules.push(Schedule.fromShadow('sh' + (i + 1), scheduleStr))
            } else {
                if (scheduleStr && scheduleStr.length > 0) {
                    console.log('schedule.loadThingSchedules, invalid schedule format ' + scheduleStr)
                }
            }
        }
        this.sort(schedules)
        this.loading = false

        return schedules;
    }

    private sort(schedules: Schedule[]): void {
        schedules.sort((a, b) => {
            let result: number = a.startDate.getTime() - b.startDate.getTime()
            if (result === 0) {
                result = a.startTime.hours - b.startTime.hours
            }
            if (result === 0) {
                result = a.startTime.minutes - b.startTime.minutes
            }
            if (result === 0) {
                result = a.endDate.getTime() - b.endDate.getTime()
            }
            if (result === 0) {
                result = a.endTime.hours - b.endTime.hours
            }
            if (result === 0) {
                result = a.endTime.minutes - b.endTime.minutes
            }
            return result
        })
    }
}



// WEBPACK FOOTER //
// /Users/mjahoo/workspace/rento/git/saunasampo-monitor/src/app/device/schedules/schedules.component.ts